/**
 * Groups feature — Zod schemas.
 *
 * Groups are named bundles of users referenced by application
 * access grants and conditional-access policy conditions. The
 * backing table is `scim_groups` — the "scim_" prefix is a legacy
 * naming artifact; the admin surface treats it as the canonical
 * groups table and both native-admin mutations and SCIM provisioning
 * write to it through the same RLS-enforced path.
 *
 * Wire shape mirrors `pkg/api/tenant/group.go` on the backend.
 */

import { z } from "zod";

export const groupStatusSchema = z.enum(["active", "deleted"]);
export type GroupStatus = z.infer<typeof groupStatusSchema>;

export const groupsListSearchSchema = z.object({
  q: z.string().optional(),
  sortBy: z.enum(["display_name", "created_at", "updated_at"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type GroupsListSearch = z.infer<typeof groupsListSearchSchema>;

export const groupSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  display_name: z.string(),
  external_id: z.string().nullable(),
  status: groupStatusSchema,
  member_count: z.number().int().nonnegative().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Group = z.infer<typeof groupSchema>;

export const groupsListResponseSchema = z.object({
  data: z.array(groupSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    // Uniform with every other list response — nonnegative, not
    // positive. See applications/schemas.ts for the rationale.
    limit: z.number().int().nonnegative(),
  }),
});
export type GroupsListResponse = z.infer<typeof groupsListResponseSchema>;

export const groupMemberSchema = z.object({
  user_id: z.string(),
  created_at: z.string(),
  // Denormalized from the users table by the backend so the
  // member list renders human-readable values without an n+1
  // user lookup per row. Both nullable: if the user row was
  // hard-deleted but the membership row survived (FK is ON
  // DELETE CASCADE — shouldn't happen — but defence in depth),
  // the UI falls back to the UUID. The backend uses omitempty
  // on these fields when null; `.nullish()` accepts both
  // absent and null.
  user_display_name: z.string().nullish(),
  user_primary_email: z.string().nullish(),
});
export type GroupMember = z.infer<typeof groupMemberSchema>;

export const groupMembersResponseSchema = z.object({
  data: z.array(groupMemberSchema),
});

export const createGroupSchema = z.object({
  display_name: z.string().min(1, "Display name is required").max(80),
  external_id: z.string().max(255).optional(),
});
export type CreateGroupInput = z.infer<typeof createGroupSchema>;

export const updateGroupSchema = z.object({
  display_name: z.string().min(1).max(80).optional(),
  external_id: z.string().max(255).nullable().optional(),
});
export type UpdateGroupInput = z.infer<typeof updateGroupSchema>;
